import { useOffices, type Office } from "@/contexts/OfficesContext";
import { useState, useRef, useCallback, useEffect } from "react";

interface OfficeFilterProps {
  offices?: Office[];
  selectedOffices?: string[];
  onSelectOffice?: (id: string) => void;
  onClearSelectedOffices?: () => void;
}

export function OfficeFilter(props: OfficeFilterProps) {
  const context = useOffices();
  const offices = props.offices ?? context.offices;
  const selectedOffices = props.selectedOffices ?? context.selectedOffices;
  const selectOffice = props.onSelectOffice ?? context.selectOffice;
  const clearSelectedOffices = props.onClearSelectedOffices ?? context.clearSelectedOffices;
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getLabel = useCallback(() => {
    const caret = isOpen ? <span className="up-caret">❮</span> : <span className="down-caret">❮</span>;
    if (selectedOffices.length === 0 || selectedOffices[0] === "all") return <>All Offices{caret}</>;
    if (selectedOffices.length === 1) {
      return <>{offices.find((o) => o.id === selectedOffices[0])?.name ?? "(1) Office"}{caret}</>;
    }
    return <>({selectedOffices.length}) Offices Selected{caret}</>;
  }, [selectedOffices, offices, isOpen]);

  const handleClearSelectedOffices = useCallback(() => {
    clearSelectedOffices();
    setIsOpen(false);
  }, [clearSelectedOffices]);

  return (
    <div ref={ref} className="office-filter">
      <button
        type="button"
        className={`office-filter__button ${isOpen ? "office-filter__button--active" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ width: "100%" }}
      >
        {getLabel()}
      </button>
      {
        isOpen &&
        <div className="office-filter__dropdown">
          <ul
            className="office-filter__dropdown-list"
          >
            {offices.map((office) => (
              <li key={office.id}>
                <label className="office-filter__option">
                  <input
                    type="checkbox"
                    checked={selectedOffices.includes(office.id)}
                    onChange={() => selectOffice(office.id)}
                  />
                  {office.name}
                </label>
              </li>
            ))}
            <hr className="divider" />
            <button className="office-filter__clear-button" disabled={selectedOffices.length === 0} onClick={handleClearSelectedOffices}>
              Clear Selected Offices
            </button>
          </ul>
        </div>
      }
    </div >
  );
}
